import {
  clamp,
  preventDefault,
  isMobileBreakpoint
} from './utilities.js';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const DEFAULT_ZOOM = 1;

export class DragZoomWrapper extends HTMLElement {
  #controller = new AbortController();

  /** @type {number} */
  #scale = DEFAULT_ZOOM;

  /** @type {number} */
  #initialDistance = 0;

  /** @type {number} */
  #startScale = DEFAULT_ZOOM;

  /** @type {Point} */
  #translate = { x: 0, y: 0 };

  /** @type {Point} */
  #startPosition = { x: 0, y: 0 };

  /** @type {Point} */
  #startTranslate = { x: 0, y: 0 };

  /** @type {boolean} */
  #isDragging = false;

  /** @type {boolean} */
  #isSwiping = false;

  /** @type {Point} */
  #swipeStartPosition = { x: 0, y: 0 };

  /** @type {Point} */
  #swipeCurrentPosition = { x: 0, y: 0 };

  /** @type {number} */
  #swipeStartScrollLeft = 0;

  /** @type {boolean} */
  #initialized = false;

  /** @type {number | null} */
  #animationFrame = null;

  get #image() {
    return this.querySelector('img');
  }

  connectedCallback() {
    if (!this.#image) return;

    this.#initResizeListener();

    if (!isMobileBreakpoint()) return;

    this.#initEventListeners();
    this.#updateTransform();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#resizeObserver.disconnect();
    this.#cancelAnimationFrame();
  }

  #initResizeListener() {
    this.#resizeObserver.observe(this);
  }

  #initEventListeners() {
    if (this.#initialized) return;

    this.#initialized = true;

    const { signal } = this.#controller;
    const options = {
      passive: false,
      signal
    };

    this.addEventListener(
      'touchstart',
      this.#handleTouchStart,
      options
    );

    this.addEventListener(
      'touchmove',
      this.#handleTouchMove,
      options
    );

    this.addEventListener(
      'touchend',
      this.#handleTouchEnd,
      options
    );

    this.addEventListener(
      'touchcancel',
      this.#handleTouchEnd,
      options
    );

    this.#image?.addEventListener(
      'load',
      this.#updateTransform,
      { signal }
    );
  }

  #handleResize = () => {
    if (
      !this.#initialized &&
      isMobileBreakpoint()
    ) {
      this.#initEventListeners();
    }

    if (this.#initialized) {
      this.#requestUpdateTransform();
    }
  };

  #resizeObserver =
    new ResizeObserver(this.#handleResize);

  /**
   * Inicia zoom, arraste ou troca de imagem.
   *
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    if (event.touches.length === 2) {
      preventDefault(event);

      this.#isSwiping = false;
      this.#isDragging = false;

      const [point1, point2] =
        Array.from(event.touches).map(
          touchToPoint
        );

      if (!point1 || !point2) return;

      this.#startZoomGesture(
        point1,
        point2
      );

      return;
    }

    if (event.touches.length !== 1) {
      return;
    }

    const point =
      touchToPoint(event.touches[0]);

    if (!point) return;

    /*
     * Em 1x, um dedo movimenta a galeria.
     * Acima de 1x, um dedo movimenta
     * a própria imagem ampliada.
     */
    if (
      this.#scale <=
      MIN_ZOOM + 0.01
    ) {
      this.#startSwipeGesture(point);
      return;
    }

    preventDefault(event);
    this.#startDragGesture(point);
  };

  /**
   * Inicia o gesto para trocar de foto.
   *
   * @param {Point} point
   */
  #startSwipeGesture(point) {
    this.#isSwiping = true;
    this.#isDragging = false;

    this.#swipeStartPosition = {
      x: point.x,
      y: point.y
    };

    this.#swipeCurrentPosition = {
      x: point.x,
      y: point.y
    };

    const gallery = this
      .closest('zoom-dialog')
      ?.querySelector(
        '.dialog-zoomed-gallery'
      );

    if (
      gallery instanceof HTMLElement
    ) {
      this.#swipeStartScrollLeft =
        gallery.scrollLeft;

      gallery.style.setProperty(
        'scroll-behavior',
        'auto'
      );
    }
  }

  /**
   * Inicia o zoom com dois dedos.
   *
   * @param {Point} point1
   * @param {Point} point2
   */
  #startZoomGesture(
    point1,
    point2
  ) {
    this.#initialDistance =
      getDistance(point1, point2);

    this.#startScale = this.#scale;
    this.#isDragging = false;
    this.#isSwiping = false;
  }

  /**
   * Inicia o movimento da imagem ampliada.
   *
   * @param {Point} point
   */
  #startDragGesture(point) {
    this.#startPosition = {
      x: point.x,
      y: point.y
    };

    this.#startTranslate = {
      x: this.#translate.x,
      y: this.#translate.y
    };

    this.#isDragging = true;
    this.#isSwiping = false;
  }

  /**
   * Processa o movimento dos dedos.
   *
   * @param {TouchEvent} event
   */
  #handleTouchMove = (event) => {
    const isZooming =
      event.touches.length === 2;

    const isDragging =
      event.touches.length === 1 &&
      this.#isDragging;

    const isSwiping =
      event.touches.length === 1 &&
      this.#isSwiping;

    if (isZooming) {
      preventDefault(event);

      this.#processZoomGesture(event);
      return;
    }

    if (isSwiping) {
      const point =
        touchToPoint(
          event.touches[0]
        );

      if (!point) return;

      this.#swipeCurrentPosition =
        point;

      const deltaX =
        point.x -
        this.#swipeStartPosition.x;

      const deltaY =
        point.y -
        this.#swipeStartPosition.y;

      /*
       * Só assume o controle quando
       * o movimento for horizontal.
       */
      if (
        Math.abs(deltaX) > 8 &&
        Math.abs(deltaX) >
          Math.abs(deltaY)
      ) {
        preventDefault(event);

        const gallery = this
          .closest('zoom-dialog')
          ?.querySelector(
            '.dialog-zoomed-gallery'
          );

        if (
          gallery instanceof
          HTMLElement
        ) {
          gallery.scrollLeft =
            this.#swipeStartScrollLeft -
            deltaX;
        }
      }

      return;
    }

    if (isDragging) {
      preventDefault(event);

      this.#processDragGesture(event);
    }
  };

  /**
   * Processa a ampliação com dois dedos.
   *
   * @param {TouchEvent} event
   */
  #processZoomGesture(event) {
    const [point1, point2] =
      Array.from(event.touches).map(
        touchToPoint
      );

    if (!point1 || !point2) return;

    const currentMidpoint =
      getMidpoint(point1, point2);

    const currentDistance =
      getDistance(point1, point2);

    const oldScale = this.#scale;

    const newScale =
      (
        currentDistance /
        this.#initialDistance
      ) * this.#startScale;

    this.#scale = clamp(
      newScale,
      MIN_ZOOM,
      MAX_ZOOM
    );

    const containerCenter = {
      x: this.clientWidth / 2,
      y: this.clientHeight / 2
    };

    const distanceFromCenter = {
      x:
        currentMidpoint.x -
        containerCenter.x,
      y:
        currentMidpoint.y -
        containerCenter.y
    };

    const scaleDelta =
      this.#scale / oldScale - 1;

    this.#translate.x -=
      (
        distanceFromCenter.x *
        scaleDelta
      ) / this.#scale;

    this.#translate.y -=
      (
        distanceFromCenter.y *
        scaleDelta
      ) / this.#scale;

    this.#requestUpdateTransform();
    this.#isDragging = false;
  }

  /**
   * Movimenta a imagem ampliada.
   *
   * @param {TouchEvent} event
   */
  #processDragGesture(event) {
    const point =
      touchToPoint(event.touches[0]);

    if (!point) return;

    this.#translate = {
      x:
        this.#startTranslate.x +
        (
          point.x -
          this.#startPosition.x
        ) / this.#scale,

      y:
        this.#startTranslate.y +
        (
          point.y -
          this.#startPosition.y
        ) / this.#scale
    };

    this.#requestUpdateTransform();
  }

  /**
   * Finaliza o gesto.
   *
   * @param {TouchEvent} event
   */
  #handleTouchEnd = (event) => {
    if (event.touches.length !== 0) {
      return;
    }

    if (this.#isSwiping) {
      const finalPoint =
        touchToPoint(
          event.changedTouches[0]
        );

      if (finalPoint) {
        this.#swipeCurrentPosition =
          finalPoint;
      }

      if (
        this.#finishSwipeGesture()
      ) {
        preventDefault(event);
      }
    }

    this.#isSwiping = false;
    this.#isDragging = false;

    this.#requestUpdateTransform();
  };

  /**
   * Seleciona a próxima foto,
   * a anterior ou retorna à atual.
   *
   * @returns {boolean}
   */
  #finishSwipeGesture() {
    const deltaX =
      this.#swipeCurrentPosition.x -
      this.#swipeStartPosition.x;

    const deltaY =
      this.#swipeCurrentPosition.y -
      this.#swipeStartPosition.y;

    const minimumDistance =
      Math.max(
        44,
        Math.min(
          72,
          this.clientWidth * 0.12
        )
      );

    const zoomDialog =
      this.closest('zoom-dialog');

    const gallery =
      zoomDialog?.querySelector(
        '.dialog-zoomed-gallery'
      );

    if (
      gallery instanceof HTMLElement
    ) {
      gallery.style.removeProperty(
        'scroll-behavior'
      );
    }

    /*
     * Movimento curto ou vertical:
     * retorna para a imagem atual.
     */
    if (
      Math.abs(deltaX) <
        minimumDistance ||
      Math.abs(deltaX) <=
        Math.abs(deltaY)
    ) {
      if (
        gallery instanceof HTMLElement
      ) {
        gallery.scrollTo({
          left:
            this
              .#swipeStartScrollLeft,

          behavior: 'smooth'
        });
      }

      return false;
    }

    if (!zoomDialog) {
      return false;
    }

    /*
     * Arrastou para a esquerda:
     * próxima imagem.
     */
    if (
      deltaX < 0 &&
      typeof zoomDialog.nextMedia ===
        'function'
    ) {
      zoomDialog.nextMedia();
      return true;
    }

    /*
     * Arrastou para a direita:
     * imagem anterior.
     */
    if (
      deltaX > 0 &&
      typeof zoomDialog
        .previousMedia === 'function'
    ) {
      zoomDialog.previousMedia();
      return true;
    }

    return false;
  }

  /**
   * A imagem sempre abre inteira em 1x.
   *
   * @returns {number}
   */
  #getMinZoom = () => {
    return MIN_ZOOM;
  };

  /**
   * Calcula as dimensões visíveis
   * da imagem dentro do container.
   *
   * @returns {{
   *   width: number,
   *   height: number
   * } | null}
   */
  #getImageDimensions = () => {
    const containerRect =
      this.getBoundingClientRect();

    if (!this.#image) return null;

    const {
      naturalWidth,
      naturalHeight
    } = this.#image;

    const containerWidth =
      this.clientWidth;

    const containerHeight =
      this.clientHeight;

    if (
      !naturalWidth ||
      !naturalHeight ||
      !containerWidth ||
      !containerHeight
    ) {
      return null;
    }

    const containerAspect =
      containerRect.width /
      containerRect.height;

    const naturalAspect =
      naturalWidth /
      naturalHeight;

    let imageWidth;
    let imageHeight;

    if (
      naturalAspect >
      containerAspect
    ) {
      imageWidth =
        containerRect.width;

      imageHeight =
        imageWidth /
        naturalAspect;
    } else {
      imageHeight =
        containerRect.height;

      imageWidth =
        imageHeight *
        naturalAspect;
    }

    if (
      imageWidth === 0 ||
      imageHeight === 0
    ) {
      return null;
    }

    return {
      width: imageWidth,
      height: imageHeight
    };
  };

  /**
   * Mantém a imagem ampliada
   * dentro dos limites da tela.
   */
  #constrainTranslation() {
    const containerWidth =
      this.clientWidth;

    const containerHeight =
      this.clientHeight;

    if (
      !containerWidth ||
      !containerHeight
    ) {
      return;
    }

    const minZoom =
      this.#getMinZoom();

    if (!minZoom) return;

    this.#scale = clamp(
      this.#scale,
      minZoom,
      MAX_ZOOM
    );

    const imageDimensions =
      this.#getImageDimensions();

    if (!imageDimensions) return;

    const {
      width: imageWidth,
      height: imageHeight
    } = imageDimensions;

    const scaledWidth =
      imageWidth * this.#scale;

    const scaledHeight =
      imageHeight * this.#scale;

    const excessWidth =
      Math.max(
        0,
        scaledWidth -
          containerWidth
      );

    const excessHeight =
      Math.max(
        0,
        scaledHeight -
          containerHeight
      );

    const maxTranslateX =
      excessWidth /
      (2 * this.#scale);

    const maxTranslateY =
      excessHeight /
      (2 * this.#scale);

    this.#translate.x = clamp(
      this.#translate.x,
      -maxTranslateX,
      maxTranslateX
    );

    this.#translate.y = clamp(
      this.#translate.y,
      -maxTranslateY,
      maxTranslateY
    );
  }

  #requestUpdateTransform = () => {
    if (!this.#animationFrame) {
      this.#animationFrame =
        requestAnimationFrame(
          this.#updateTransform
        );
    }
  };

  #cancelAnimationFrame() {
    if (this.#animationFrame) {
      cancelAnimationFrame(
        this.#animationFrame
      );

      this.#animationFrame = null;
    }
  }

  #updateTransform = () => {
    this.#animationFrame = null;

    this.#constrainTranslation();

    this.style.setProperty(
      '--drag-zoom-scale',
      this.#scale.toString()
    );

    this.style.setProperty(
      '--drag-zoom-translate-x',
      `${this.#translate.x}px`
    );

    this.style.setProperty(
      '--drag-zoom-translate-y',
      `${this.#translate.y}px`
    );
  };

  destroy() {
    this.#controller.abort();
    this.#cancelAnimationFrame();
  }
}

if (
  !customElements.get(
    'drag-zoom-wrapper'
  )
) {
  customElements.define(
    'drag-zoom-wrapper',
    DragZoomWrapper
  );
}

/**
 * @typedef {Object} Point
 * @property {number} x
 * @property {number} y
 */

/**
 * @param {Touch | undefined} touch
 * @returns {Point | undefined}
 */
function touchToPoint(touch) {
  if (!touch) return undefined;

  return {
    x: touch.clientX,
    y: touch.clientY
  };
}

/**
 * @param {Point} point1
 * @param {Point} point2
 * @returns {number}
 */
function getDistance(
  point1,
  point2
) {
  const deltaX =
    point1.x - point2.x;

  const deltaY =
    point1.y - point2.y;

  return Math.sqrt(
    deltaX * deltaX +
    deltaY * deltaY
  );
}

/**
 * @param {Point} point1
 * @param {Point} point2
 * @returns {Point}
 */
function getMidpoint(
  point1,
  point2
) {
  return {
    x:
      (
        point1.x +
        point2.x
      ) / 2,

    y:
      (
        point1.y +
        point2.y
      ) / 2
  };
}